import { defineStore } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'

import { useApiStore } from '@/stores/api'
import { useDomainStore } from '@/stores/domain'
import { useEnvironmentStore } from '@/stores/environment'

import type { Group } from '@policyfly/protobuf/pfbingo'

export const useGroupStore = defineStore('group', () => {
  const apiStore = useApiStore()
  const domainStore = useDomainStore()
  const environmentStore = useEnvironmentStore()

  const groupsByEnvironmentRef = shallowRef<Record<string, Group[]>>({})
  const loading = ref(false)
  let groupsByEnvironmentRequest = 0

  async function loadGroupsByEnvironment (): Promise<void> {
    const request = ++groupsByEnvironmentRequest
    const domain = domainStore.currentDomain
    const environments = environmentStore.environments

    groupsByEnvironmentRef.value = {}
    if (!environments.length) return

    loading.value = true
    try {
      const entries = await Promise.all(environments.map(async (environment) => {
        const { groups } = await apiStore.listGroupsForEnvironment({
          environmentId: environment.id,
          domain,
        })
        return [environment.id, groups] as const
      }))

      // The selected domain or environment list may have changed while requests ran.
      if (request !== groupsByEnvironmentRequest) return
      groupsByEnvironmentRef.value = Object.fromEntries(entries)
    } catch (error) {
      if (request === groupsByEnvironmentRequest) {
        console.error('Failed to load groups for environments:', error)
      }
    } finally {
      if (request === groupsByEnvironmentRequest) loading.value = false
    }
  }

  watch(
    [() => environmentStore.environments, () => domainStore.currentDomain],
    loadGroupsByEnvironment,
    { immediate: true },
  )

  const groupsByEnvironment = computed(() => groupsByEnvironmentRef.value)

  return {
    groupsByEnvironment,
    loading,
    loadGroupsByEnvironment,
  }
})
